import React, { useEffect, useRef, useState } from "react";
import RetirementAgeSelector from "./RetirementAgeSelector";
import RetirementStepsTitle from "./RetirementStepsTitle";
import {
  MIN_CURRENT_AGE,
  MAX_CURRENT_AGE,
  DEFAULT_MIN_RETIREMENT_AGE,
  MAX_RETIREMENT_AGE,
} from "@/utils/financialPageFunctions";

export default function RetirementAgeScreen({
  stepTitle,
  currentAgeLabel,
  retirementAgeLabel,
  currentAge,
  retirementAge,
  onCurrentAge,
  onRetirementAge,
  validateTrigger,
}: {
  stepTitle?: string;
  currentAgeLabel?: string;
  retirementAgeLabel?: string;
  currentAge: number;
  retirementAge: number;
  onCurrentAge: (v: number) => void;
  onRetirementAge: (v: number) => void;
  validateTrigger?: number;
}) {
  const [currentAgeError, setCurrentAgeError] = useState("");
  const [retirementAgeError, setRetirementAgeError] = useState("");

  const minRetirementAge = currentAge > 40 ? currentAge + 5 : DEFAULT_MIN_RETIREMENT_AGE;

  const validateCurrentAge = (age: number) => {
    if (isNaN(age) || age < MIN_CURRENT_AGE) {
      setCurrentAgeError(`Current age must be at least ${MIN_CURRENT_AGE} years.`);
      return false;
    }
    if (age > MAX_CURRENT_AGE) {
      setCurrentAgeError(`Current age cannot exceed ${MAX_CURRENT_AGE} years.`);
      return false;
    }
    setCurrentAgeError("");
    return true;
  };

  const validateRetirementAge = (age: number) => {
    if (isNaN(age) || age < minRetirementAge) {
      setRetirementAgeError(`Retirement age must be at least ${minRetirementAge} years.`);
      return false;
    }
    if (age > MAX_RETIREMENT_AGE) {
      setRetirementAgeError(`Retirement age cannot exceed ${MAX_RETIREMENT_AGE} years.`);
      return false;
    }
    setRetirementAgeError("");
    return true;
  };

  // When modal's Continue is clicked and validation fails, reveal all errors
  const prevTrigger = useRef(0);
  const currentAgeRef = useRef(currentAge);
  const retirementAgeRef = useRef(retirementAge);
  currentAgeRef.current = currentAge;
  retirementAgeRef.current = retirementAge;

  useEffect(() => {
    if (!validateTrigger || validateTrigger === prevTrigger.current) return;
    prevTrigger.current = validateTrigger;
    validateCurrentAge(currentAgeRef.current);
    validateRetirementAge(retirementAgeRef.current);
  }, [validateTrigger]);

  return (
    <div className="flex flex-col">
      <RetirementStepsTitle stepTitle={stepTitle || "Select Current & Retirement Age"} />

      <RetirementAgeSelector
        label={currentAgeLabel || "Your Current Age is"}
        value={currentAge}
        min={MIN_CURRENT_AGE}
        max={MAX_CURRENT_AGE}
        onChange={(age) => {
          setCurrentAgeError("");
          onCurrentAge(age);
        }}
        onBlur={(age) => validateCurrentAge(age)}
      />
      {currentAgeError && <p className="text-xs text-red-500 mt-1.5">{currentAgeError}</p>}

      <div className="border-t border-dashed border-[#8D8D8D] my-4 sm:my-8" />

      <RetirementAgeSelector
        label={retirementAgeLabel || "Your Retirement Age is"}
        value={retirementAge}
        min={minRetirementAge}
        max={MAX_RETIREMENT_AGE}
        onChange={(age) => {
          setRetirementAgeError("");
          onRetirementAge(age);
        }}
        onBlur={(age) => validateRetirementAge(age)}
        valueColor="text-[#003D6C]"
      />
      {retirementAgeError && <p className="text-xs text-red-500 mt-1.5">{retirementAgeError}</p>}
    </div>
  );
}
